import { Button } from "@/components/generic/Button";
import { RadioCard } from "@/components/generic/RadioCard";
import {
  getAvailableIntegrationsQueryOptions,
  getIntegrationsQueryOptions,
} from "@/queries/integrations";
import { Campaign } from "@/types/campaign";
import { StepperSubmitHandler } from "@/utils/stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { PlusIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { RadioGroup } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { SegmentHeader } from "../../basic/SegmentHeader";

export function SelectIntegrationForm({
  exportSystem,
  updateExportSystem,
  setSubmitHandler,
  onSetupIntegration,
}: {
  exportSystem: Campaign["exportSystem"];
  updateExportSystem: (exportSystem: Campaign["exportSystem"]) => void;
  setSubmitHandler?: (submitHandler: StepperSubmitHandler) => void;
  onSetupIntegration: () => void;
}) {
  const { data: availableIntegrations } = useQuery(
    getAvailableIntegrationsQueryOptions(),
  );
  const { data: integrationProvidersQueryData } = useQuery(
    getIntegrationsQueryOptions(),
  );
  const integrationProviders = useMemo(() => {
    if (!integrationProvidersQueryData || !availableIntegrations) return null;
    return integrationProvidersQueryData.filter((provider) => {
      const availInte = availableIntegrations.find(
        (inte) =>
          inte.uniqueName.toLowerCase() === provider.provider.toLowerCase(),
      );
      if (!availInte) return false;
      return (
        ["CRM", "Dialer"].includes(availInte.category) ||
        availInte.uniqueName.toLowerCase() === "customapi"
      );
    });
  }, [availableIntegrations, integrationProvidersQueryData]);

  const availableIntegrationsMap = useMemo(
    () =>
      new Map(
        availableIntegrations?.map((integration) => [
          integration.uniqueName.toLowerCase(),
          integration,
        ]),
      ),
    [availableIntegrations],
  );

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: useMemo(
      () =>
        zodResolver(
          z.object({
            exportSystem: z
              .string()
              .nullable()
              .refine((value) => value !== null, "Vælg venligst et CRM-system"),
          }),
        ),
      [],
    ),
    defaultValues: {
      exportSystem: exportSystem ?? null,
    },
  });

  useEffect(() => {
    reset();
  }, [exportSystem, reset]);

  const submit = useMemo(
    () =>
      handleSubmit((data) => {
        updateExportSystem(data.exportSystem);
      }),
    [handleSubmit, updateExportSystem],
  );

  useEffect(() => {
    setSubmitHandler?.({
      submit,
      isDisabled: !isValid,
    });
  }, [isValid, setSubmitHandler, submit]);

  return (
    <AnimatePresence initial={false} mode="popLayout">
      {!availableIntegrations || !integrationProviders ? (
        <motion.div
          key="loading"
          className="flex animate-pulse items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          Indlæser...
        </motion.div>
      ) : integrationProviders.length ? (
        <motion.div
          key="select-integration"
          className="flex flex-col items-center gap-y-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <SegmentHeader
            title="Vælg integration"
            description="Her skal du vælge det system som dine leads skal sendes til"
          />

          <Controller
            control={control}
            name="exportSystem"
            render={({ field }) => (
              <RadioGroup
                className="grid w-full grid-cols-[repeat(auto-fill,minmax(min(100%,200px),1fr))] justify-center gap-6"
                value={field.value}
                onChange={field.onChange}
                aria-label="Vælg metode"
              >
                {integrationProviders.map((integrationProvider) => {
                  const integration = availableIntegrationsMap.get(
                    integrationProvider.provider.toLowerCase(),
                  );

                  return (
                    <RadioCard
                      key={integrationProvider.provider}
                      className="flex size-full flex-col items-center justify-center gap-y-6 rounded-lg p-8"
                      value={integrationProvider.provider}
                      onPress={() => {
                        field.onChange(integrationProvider.provider);
                        submit();
                      }}
                      aria-label={integrationProvider.providerName}
                    >
                      {integration?.iconSVG && (
                        <div
                          className="shrink-0 *:size-12"
                          dangerouslySetInnerHTML={{
                            __html: integration.iconSVG,
                          }}
                        />
                      )}

                      <div className="flex flex-col gap-y-4">
                        <div className="text-center text-sm/normal font-semibold">
                          {integrationProvider.providerName}
                        </div>
                      </div>
                    </RadioCard>
                  );
                })}

                <RadioCard
                  className="flex size-full flex-col items-center justify-center gap-y-6 rounded-lg p-8"
                  value=""
                  onPress={() => {
                    field.onChange(null);
                    onSetupIntegration();
                  }}
                  aria-label={"Opsæt integration"}
                >
                  <PlusIcon className="size-12 shrink-0 text-[#616161]" />

                  <div className="flex flex-col gap-y-4">
                    <div className="text-center text-sm/normal font-semibold">
                      Opsæt integration
                    </div>
                  </div>
                </RadioCard>
              </RadioGroup>
            )}
          />
        </motion.div>
      ) : (
        <motion.div
          key="no-integrations"
          className="flex flex-col items-center gap-y-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <SegmentHeader
            className="max-w-lg"
            title="Opsæt integration"
            description="Klik på knappen for at opsætte en integration. Når du er færdig sendes du automatisk tilbage til denne side."
          />

          <Button onPress={onSetupIntegration}>Opsæt nu</Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
